import React from "react";
import { Card, CardContent, Typography, Box, Divider } from "@mui/material";

/**
 * Reusable account/credit item card with border and rounded corners, transparent bg.
 * Props:
 * - title: string (e.g., "Chase Bank")
 * - subtitle: string (e.g., "****1234 • Checking")
 * - amount: string (formatted) (e.g., "$15,000")
 * - amountColor: one of theme palette keys or any CSS color (e.g., "success.main", "error.main", "#00B" )
 * - rowsTop: array of { label, value } shown above divider
 * - rowsBottom: array of { label, value } shown below divider (bold values)
 * - rightCaption: string (small caption under amount; optional)
 */
export default function AccountItemCard(props) {
  const {
    title,
    subtitle = "",
    amount,
    amountColor = "text.primary",
    rightCaption = "",
    rowsTop = [],
    rowsBottom = [],
  } = props;

  return (
    <Card
      sx={{
        bgcolor: "transparent",
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 0,
      }}
    >
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
            ) : null}
          </Box>
          <Box sx={{ textAlign: "right", minWidth: 140 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: amountColor }}>
              {amount}
            </Typography>
            {rightCaption ? (
              <Typography variant="caption" color="text.secondary">{rightCaption}</Typography>
            ) : null}
          </Box>
        </Box>

        {rowsTop?.length ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 0.25 }}>
            {rowsTop.map((r, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                {r.label}: {r.value}
              </Typography>
            ))}
          </Box>
        ) : null}

        {rowsBottom?.length ? <Divider sx={{ my: 0.5 }} /> : null}

        {rowsBottom?.length ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 0.25 }}>
            {rowsBottom.map((r, i) => (
              <Typography key={i} variant="body2">
                {r.label}: <strong>{r.value}</strong>
              </Typography>
            ))}
          </Box>
        ) : null}
      </CardContent>
    </Card>
  );
}
